/** XeAgents.ai **/
import { Card } from "@/components/ui/card";
import { MetricCard } from "@/components/MetricCard";
import { AnalyticsChart } from "@/components/AnalyticsChart";
import { Coins, Calculator, Target, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Call {
  id: string;
  status: string;
  classification: string | null;
  creditsDeducted: number | null;
}

interface CampaignCostDashboardProps {
  calls: Call[];
}

export function CampaignCostDashboard({ calls }: CampaignCostDashboardProps) {
  const { t } = useTranslation();

  // Calculate credit metrics
  const totalCredits = calls.reduce((sum, c) => sum + (c.creditsDeducted || 0), 0);
  const completedCalls = calls.filter(c => c.status === 'completed');
  const creditsPerCall = completedCalls.length > 0
    ? (totalCredits / completedCalls.length).toFixed(1)
    : "0";

  const qualifiedLeads = calls.filter(c =>
    c.classification === 'hot' || c.classification === 'warm'
  );
  const creditsPerLead = qualifiedLeads.length > 0
    ? (totalCredits / qualifiedLeads.length).toFixed(1)
    : "0";

  // Credit usage by outcome
  const creditsByOutcome = [
    {
      name: "Hot Leads",
      value: calls.filter(c => c.classification === 'hot')
        .reduce((s, c) => s + (c.creditsDeducted || 0), 0),
    },
    {
      name: "Warm Leads",
      value: calls.filter(c => c.classification === 'warm')
        .reduce((s, c) => s + (c.creditsDeducted || 0), 0),
    },
    {
      name: "Cold Leads",
      value: calls.filter(c => c.classification === 'cold')
        .reduce((s, c) => s + (c.creditsDeducted || 0), 0),
    },
    {
      name: "No Response",
      value: calls.filter(c => ['no-answer', 'busy', 'failed'].includes(c.status))
        .reduce((s, c) => s + (c.creditsDeducted || 0), 0),
    },
  ].filter(item => item.value > 0);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Credit Usage</h3>

      {/* Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Credits Used"
          value={totalCredits.toFixed(1)}
          icon={Coins}
          subtitle={`${completedCalls.length} completed calls`}
          testId="metric-total-credits"
        />
        <MetricCard
          title="Credits per Call"
          value={creditsPerCall}
          icon={Calculator}
          subtitle="Average per completed call"
          testId="metric-credits-per-call"
        />
        <MetricCard
          title="Credits per Lead"
          value={creditsPerLead}
          icon={Target}
          subtitle={`${qualifiedLeads.length} qualified leads`}
          testId="metric-credits-per-lead"
        />
        <MetricCard
          title="Qualified Leads"
          value={qualifiedLeads.length}
          icon={TrendingUp}
          subtitle="Hot + Warm leads"
          testId="metric-qualified-leads"
        />
      </div>

      {/* Credit Usage by Outcome Chart */}
      {creditsByOutcome.length > 0 && (
        <AnalyticsChart
          title="Credit Usage by Outcome"
          type="pie"
          data={creditsByOutcome}
          testId="chart-credits-by-outcome"
        />
      )}
    </div>
  );
}
